/**
 * 大运 / 流年 / 流月，三级联动。
 *
 * 旧站导航里 decade / annual / month 是三个独立入口，但它们都依赖同一份排盘输入，
 * 拆成三页会让用户重复填生日。这里合成排盘结果下的一个面板：
 * 点大运展开该步流年，点流年展开该年流月。数据同样由 lunar-javascript 本地算出。
 *
 * 措辞注意：只陈列干支与年龄区间，不写「大运吉凶」「流年运势」一类的判断。
 * 备案登记的服务类别是「文化、教育、学习资料类网站」，越界即不合规。
 */
import { useMemo, useState } from "react";
import { Solar, type DaYun, type LiuNian } from "lunar-javascript";

type Props = {
  date: string;
  time: string;
  gender: "male" | "female";
};

export function FortunePanel({ date, time, gender }: Props) {
  const [activeDaYun, setActiveDaYun] = useState<number | null>(null);
  const [activeLiuNian, setActiveLiuNian] = useState<number | null>(null);

  const data = useMemo(() => {
    const [y, m, d] = date.split("-").map(Number);
    const [hh, mm] = time.split(":").map(Number);
    if (![y, m, d, hh, mm].every((n) => Number.isFinite(n))) return null;
    const eightChar = Solar.fromYmdHms(y, m, d, hh, mm, 0).getLunar().getEightChar();
    // lunar 的性别参数：1=男 0=女，决定大运顺排还是逆排
    const yun = eightChar.getYun(gender === "male" ? 1 : 0);
    return {
      startYear: yun.getStartYear(),
      startMonth: yun.getStartMonth(),
      // 第一步是出生到起运之间的空档（getGanZhi() 为空），不作为大运展示
      daYunList: yun.getDaYun().filter((item) => item.getGanZhi()),
    };
  }, [date, time, gender]);

  if (!data) return null;

  const liuNianList: LiuNian[] =
    activeDaYun === null ? [] : (data.daYunList[activeDaYun] as DaYun).getLiuNian();
  const liuYueList =
    activeLiuNian === null || !liuNianList[activeLiuNian] ? [] : liuNianList[activeLiuNian].getLiuYue();

  return (
    <section className="fortune-panel">
      <h2>大运 / 流年 / 流月</h2>
      <p className="fortune-start">
        起运：出生后 {data.startYear} 年 {data.startMonth} 个月
      </p>

      <div className="fortune-row" role="tablist" aria-label="大运">
        {data.daYunList.map((item, index) => (
          <button
            key={item.getStartYear()}
            type="button"
            role="tab"
            aria-selected={activeDaYun === index}
            className={activeDaYun === index ? "active" : undefined}
            onClick={() => {
              setActiveDaYun(activeDaYun === index ? null : index);
              setActiveLiuNian(null);
            }}
          >
            <span className="fortune-ganzhi">{item.getGanZhi()}</span>
            <span className="fortune-meta">
              {item.getStartAge()}–{item.getEndAge()} 岁
            </span>
            <span className="fortune-meta">{item.getStartYear()}</span>
          </button>
        ))}
      </div>

      {activeDaYun !== null && (
        <>
          <h3>
            {data.daYunList[activeDaYun].getGanZhi()} 大运 · 流年
          </h3>
          <div className="fortune-row" role="tablist" aria-label="流年">
            {liuNianList.map((item, index) => (
              <button
                key={item.getYear()}
                type="button"
                role="tab"
                aria-selected={activeLiuNian === index}
                className={activeLiuNian === index ? "active" : undefined}
                onClick={() => setActiveLiuNian(activeLiuNian === index ? null : index)}
              >
                <span className="fortune-ganzhi">{item.getGanZhi()}</span>
                <span className="fortune-meta">{item.getYear()}</span>
                <span className="fortune-meta">{item.getAge()} 岁</span>
              </button>
            ))}
          </div>
        </>
      )}

      {activeLiuNian !== null && liuYueList.length > 0 && (
        <>
          <h3>{liuNianList[activeLiuNian].getYear()} 年 · 流月</h3>
          <div className="fortune-row fortune-month" aria-label="流月">
            {liuYueList.map((item, index) => (
              <div key={index} className="fortune-cell">
                <span className="fortune-ganzhi">{item.getGanZhi()}</span>
                <span className="fortune-meta">{item.getMonthInChinese()}月</span>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
